import { useScienceTutorSection } from './scienceTutorContext';
import { ArrowRight, ListChecks } from 'lucide-react';
import { useState } from 'react';

import {
  PROTON_ROLES,
  ROLE_LABELS,
  checkConjugateRoles,
  conjugatePairs,
  type ProtonRole,
  type RoleCheck,
} from './acidBaseCalculations';
import { CONJUGATE_CASES } from './acidBaseCalculationsData';
import { AcidBaseFeedback } from './AcidBaseCalculationsVisuals';

const EMPTY_ROLES: (ProtonRole | '')[] = ['', '', '', ''];

const THEORY_CARDS = [
  {
    name: 'Arrhenius theory',
    text: 'An acid releases H⁺ ions in water; a base releases OH⁻ ions in water. It only works in water.',
  },
  {
    name: 'Brønsted–Lowry theory',
    text: 'An acid donates a proton (H⁺); a base accepts one. Water is not needed, and a species can be amphiprotic.',
  },
];

export function AcidBaseCalculationsTheory() {
  const [caseId, setCaseId] = useState(CONJUGATE_CASES[0].id);
  const [roles, setRoles] = useState<(ProtonRole | '')[]>(EMPTY_ROLES);
  useScienceTutorSection('AcidBaseCalculationsTheory', { caseId, roles });
  const [check, setCheck] = useState<RoleCheck | null>(null);
  const item = CONJUGATE_CASES.find((candidate) => candidate.id === caseId) ?? CONJUGATE_CASES[0];
  const pairs = conjugatePairs(item);
  const solved = check?.status === 'correct';

  const choose = (id: string) => {
    setCaseId(id);
    setRoles(EMPTY_ROLES);
    setCheck(null);
  };

  const setRole = (index: number, role: ProtonRole | '') => {
    setRoles((current) => current.map((value, position) => (position === index ? role : value)));
    setCheck(null);
  };

  return (
    <section
      aria-label="Conjugate pair practice"
      className="rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6 lg:p-8"
    >
      <div className="grid gap-3 md:grid-cols-2">
        {THEORY_CARDS.map((card) => (
          <div key={card.name} className="rounded-[1.5rem] bg-surface p-4">
            <p className="text-sm font-extrabold text-ink">{card.name}</p>
            <p className="mt-1 text-sm font-medium leading-6 text-slate2">{card.text}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 flex items-center gap-3">
        <span className="grid h-11 w-11 place-items-center rounded-2xl bg-wash-sunshine text-ink">
          <ListChecks className="h-5 w-5" aria-hidden="true" />
        </span>
        <div>
          <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">
            Label every species
          </p>
          <h2 className="text-xl font-extrabold text-ink">
            {CONJUGATE_CASES.length} reviewed reactions
          </h2>
        </div>
      </div>
      <div className="mt-5 flex flex-wrap gap-2" role="group" aria-label="Choose a reaction">
        {CONJUGATE_CASES.map((candidate, index) => (
          <button
            key={candidate.id}
            type="button"
            aria-pressed={candidate.id === item.id}
            onClick={() => choose(candidate.id)}
            title={candidate.title}
            className={`rounded-full border px-3 py-1.5 text-sm font-extrabold transition-colors motion-reduce:transition-none ${
              candidate.id === item.id
                ? 'border-brand-sky bg-wash-sky text-ink'
                : 'border-hairline bg-white text-slate2 hover:border-brand-sky/40'
            }`}
          >
            {index + 1}
            <span className="sr-only">. {candidate.title}</span>
          </button>
        ))}
      </div>

      <h3 className="mt-6 text-lg font-extrabold text-ink">{item.title}</h3>
      <p className="mt-1 font-medium leading-7 text-ink-soft">{item.context}</p>

      <div
        className="mt-4 grid grid-cols-2 items-end gap-3 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)_auto_minmax(0,1fr)_auto_minmax(0,1fr)]"
        aria-label="Equation with a role for each species"
        role="group"
      >
        {item.species.flatMap((species, index) => {
          const separator = index === 0 || index === 2 ? '+' : index === 1 ? item.arrow : null;
          const marked = check && check.status !== 'invalid' ? check.positions[index] : null;
          return [
            <div key={`species-${species.formula}-${index}`} className="min-w-0">
              <p
                className={`rounded-2xl border-2 px-3 py-3 text-center text-xl font-extrabold text-ink ${
                  marked === null
                    ? 'border-hairline bg-surface'
                    : marked
                      ? 'border-brand-mint bg-wash-mint'
                      : 'border-dashed border-brand-coral bg-wash-coral'
                }`}
              >
                {species.formula}
                {marked === null ? null : (
                  <span className="sr-only">
                    {marked ? ' (correct role)' : ' (check this role)'}
                  </span>
                )}
              </p>
              <label className="mt-2 block text-xs font-extrabold text-slate2">
                <span className="sr-only">
                  Role of {species.formula} (position {index + 1})
                </span>
                <select
                  value={roles[index]}
                  onChange={(event) => setRole(index, event.target.value as ProtonRole | '')}
                  className="mt-1 w-full rounded-xl border border-hairline bg-white px-2 py-2 text-sm font-bold text-ink focus:border-brand-sky focus:outline-none focus:ring-4 focus:ring-brand-sky/15"
                >
                  <option value="">Choose…</option>
                  {PROTON_ROLES.map((role) => (
                    <option key={role} value={role}>
                      {ROLE_LABELS[role]}
                    </option>
                  ))}
                </select>
              </label>
            </div>,
            separator ? (
              <span
                key={`separator-${index}`}
                className="hidden pb-14 text-center text-2xl font-extrabold text-slate2 sm:block"
                aria-hidden="true"
              >
                {separator}
              </span>
            ) : null,
          ];
        })}
      </div>
      <p className="mt-2 text-center text-sm font-bold text-slate2 sm:hidden" aria-hidden="true">
        {item.species[0].formula} + {item.species[1].formula} {item.arrow} {item.species[2].formula}{' '}
        + {item.species[3].formula}
      </p>

      <button
        type="button"
        onClick={() => setCheck(checkConjugateRoles(item, roles))}
        className="mt-5 rounded-full bg-ink px-5 py-3 text-sm font-extrabold text-white"
      >
        Check roles
      </button>
      {check ? (
        <div className="mt-4">
          <AcidBaseFeedback check={check} working={[]} />
        </div>
      ) : null}

      {solved && pairs ? (
        <div
          className="mt-4 grid gap-3 md:grid-cols-2"
          role="img"
          aria-label={`Conjugate pairs: ${pairs.acid} loses H⁺ to become ${pairs.conjugateBase}; ${pairs.base} gains H⁺ to become ${pairs.conjugateAcid}.`}
        >
          {[
            {
              from: pairs.acid,
              to: pairs.conjugateBase,
              change: '− H⁺',
              label: 'Acid → conjugate base',
            },
            {
              from: pairs.base,
              to: pairs.conjugateAcid,
              change: '+ H⁺',
              label: 'Base → conjugate acid',
            },
          ].map((pair) => (
            <div
              key={pair.label}
              className="rounded-[1.5rem] border border-brand-mint/45 bg-wash-mint p-4"
            >
              <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-slate2">
                {pair.label}
              </p>
              <p className="mt-2 flex flex-wrap items-center gap-2 text-lg font-extrabold text-ink">
                {pair.from}
                <span className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-xs">
                  {pair.change} <ArrowRight className="h-3 w-3" aria-hidden="true" />
                </span>
                {pair.to}
              </p>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}
